import type { PostHogContextRef } from '../context'
import { buildInChatContextProperties } from '../context'

/**
 * Input method types for ai_message_sent event.
 */
export type AiInputMethod = 'Text' | 'Speech' | 'Prompt' | 'Card'

/**
 * Submission method types for ai_message_sent event.
 */
export type AiSubmissionMethod = 'keyboard' | 'click'

/**
 * UI affordances for ai_message_feedback. Title Case per the AI-768
 * standardized vocabulary; backend `OracleThumbValue` ('up'/'down') is
 * mapped at the call site.
 */
export type AiFeedbackUiElement = 'Up' | 'Down'

/**
 * Catalog payload for ai_recommendation_viewed / _clicked. All eight
 * properties are spec-required; `null` is accepted for fields that the
 * backend cannot supply for the recommended item (e.g.,
 * `parent_series_id` for standalone media).
 */
export interface AiRecommendationPayload {
  ui_element: 'Cover Tile' | 'Hyperlink'
  content_id: string
  content_type: string
  content_name: string
  parent_series_id: string | null
  parent_series_name: string | null
  media_id: string | null
  media_language: string | null
  is_featured_media: boolean | null
}

/**
 * AI chat / conversation / video-guide events.
 * Fire from `lib/components/OracleSideGuide/`, `lib/components/OraclePage/`, and
 * related AI-conversation surfaces.
 */
export type ChatEvents = {
  ai_cancel_modal_cancel_clicked: () => void
  ai_cancel_modal_engaged: () => void
  ai_cancel_modal_viewed: () => void
  ai_conversation_deleted: () => void
  ai_conversation_favorited: () => void
  ai_conversation_name_updated: () => void
  ai_conversation_opened: () => void
  ai_conversation_resumed: (inputMethod: AiInputMethod, uiElement: string) => void
  ai_conversation_started: (inputMethod: AiInputMethod, uiElement: string) => void
  ai_conversation_unfavorited: () => void
  ai_error_response: (
    errorDetails: string,
    inputMethod: AiInputMethod,
    uiElement: string,
  ) => void
  ai_message_failure: (
    errorDetails: string,
    inputMethod: AiInputMethod,
    uiElement: string,
  ) => void
  ai_message_received: (
    messagePairId: string,
    messageLength: number,
    inputMethod: AiInputMethod,
  ) => void
  ai_message_regenerated: (uiElement: string) => void
  ai_message_sent: (
    message: string,
    inputMethod: AiInputMethod,
    uiElement: string,
    submissionMethod?: AiSubmissionMethod
  ) => void
  ai_module_clicked: (uiElement: string) => void
  ai_module_closed: (sessionDurationMs: number | null, uiElement: string, method?: string) => void
  ai_module_opened: (uiElement: string, isInitial?: boolean) => void
  ai_module_viewed: (uiElement: string) => void
  ai_navigation_clicked: () => void
  ai_page_view_chat_home: () => void
  ai_persona_selected: (aiPersona: string) => void
  ai_rate_limited: () => void
  ai_recommendation_clicked: (payload: AiRecommendationPayload) => void
  ai_message_feedback: (uiElement: AiFeedbackUiElement) => void
  ai_recommendation_viewed: (payload: AiRecommendationPayload) => void
  ai_speech_to_text: () => void
  ai_unrecognized_input: (
    errorDetails: string,
    inputMethod: AiInputMethod,
    uiElement: string,
  ) => void
  ai_videoguide_disabled: () => void
  ai_videoguide_enabled: () => void
  concept_moment_toast_shown: (momentStartSeconds: number, conceptLabel: string) => void
  concept_moment_toast_clicked: (momentStartSeconds: number, conceptLabel: string) => void
  concept_moment_toast_dismissed: (momentStartSeconds: number, conceptLabel: string) => void
}

export function createChatEventCallbacks (
  contextRef: PostHogContextRef,
): ChatEvents {
  return {
    ai_cancel_modal_cancel_clicked: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_cancel_modal_cancel_clicked', {
        ...buildInChatContextProperties(ctx),
      })
    },

    ai_cancel_modal_engaged: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_cancel_modal_engaged', {
        ...buildInChatContextProperties(ctx),
      })
    },

    ai_cancel_modal_viewed: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_cancel_modal_viewed', {
        ...buildInChatContextProperties(ctx),
      })
    },

    ai_conversation_deleted: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_conversation_deleted', {
        ...buildInChatContextProperties(ctx),
        ui_element: 'Conversation List',
      })
    },

    ai_conversation_favorited: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_conversation_favorited', {
        ...buildInChatContextProperties(ctx),
        ui_element: 'Conversation List',
      })
    },

    ai_conversation_name_updated: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_conversation_name_updated', {
        ...buildInChatContextProperties(ctx),
        ui_element: 'Conversation List',
      })
    },

    ai_conversation_opened: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_conversation_opened', {
        ...buildInChatContextProperties(ctx),
        ui_element: 'Conversation List',
      })
    },

    ai_conversation_resumed: (inputMethod: AiInputMethod, uiElement: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_conversation_resumed', {
        ...buildInChatContextProperties(ctx),
        input_method: inputMethod,
        ui_element: uiElement,
      })
    },

    ai_conversation_started: (inputMethod: AiInputMethod, uiElement: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_conversation_started', {
        ...buildInChatContextProperties(ctx),
        input_method: inputMethod,
        ui_element: uiElement,
      })
    },

    ai_conversation_unfavorited: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_conversation_unfavorited', {
        ...buildInChatContextProperties(ctx),
        ui_element: 'Conversation List',
      })
    },

    ai_error_response: (
      errorDetails: string,
      inputMethod: AiInputMethod,
      uiElement: string,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_error_response', {
        ...buildInChatContextProperties(ctx),
        error_details: errorDetails,
        input_method: inputMethod,
        ui_element: uiElement,
      })
    },

    ai_message_failure: (
      errorDetails: string,
      inputMethod: AiInputMethod,
      uiElement: string,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_message_failure', {
        ...buildInChatContextProperties(ctx),
        error_details: errorDetails,
        input_method: inputMethod,
        ui_element: uiElement,
      })
    },

    ai_message_received: (
      messagePairId: string,
      messageLength: number,
      inputMethod: AiInputMethod,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_message_received', {
        ...buildInChatContextProperties(ctx),
        message_pair_id: messagePairId,
        message_length: messageLength,
        input_method: inputMethod,
        ui_element: 'Text',
      })
    },

    ai_message_sent: (
      message: string,
      inputMethod: AiInputMethod,
      uiElement: string,
      submissionMethod?: AiSubmissionMethod,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return

      ctx.posthogClient.capture('ai_message_sent', {
        ...buildInChatContextProperties(ctx),
        message_length: Array.from(message).length,
        input_method: inputMethod,
        ui_element: uiElement,
        ...(submissionMethod && { submission_method: submissionMethod }),
      })
    },

    ai_module_clicked: (uiElement: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_module_clicked', {
        ...buildInChatContextProperties(ctx),
        ui_element: uiElement,
      })
    },

    ai_module_closed: (sessionDurationMs: number | null, uiElement: string, method?: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_module_closed', {
        ...buildInChatContextProperties(ctx),
        session_duration_ms: sessionDurationMs,
        ui_element: uiElement,
        ...(method && { method }),
      })
    },

    ai_module_opened: (uiElement: string, isInitial?: boolean) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_module_opened', {
        ...buildInChatContextProperties(ctx),
        ui_element: uiElement,
        ...(isInitial !== undefined && { is_initial: isInitial }),
      })
    },

    ai_module_viewed: (uiElement: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_module_viewed', {
        ...buildInChatContextProperties(ctx),
        ui_element: uiElement,
      })
    },

    ai_navigation_clicked: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_navigation_clicked', {
        ...buildInChatContextProperties(ctx),
      })
    },

    ai_page_view_chat_home: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_page_view_chat_home', {
        ...buildInChatContextProperties(ctx),
      })
    },

    ai_persona_selected: (aiPersona: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_persona_selected', {
        ...buildInChatContextProperties(ctx),
        ai_persona: aiPersona,
      })
    },

    ai_rate_limited: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_rate_limited', {
        ...buildInChatContextProperties(ctx),
      })
    },

    ai_recommendation_clicked: (payload: AiRecommendationPayload) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_recommendation_clicked', {
        ...buildInChatContextProperties(ctx),
        ...payload,
      })
    },

    ai_recommendation_viewed: (payload: AiRecommendationPayload) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_recommendation_viewed', {
        ...buildInChatContextProperties(ctx),
        ...payload,
      })
    },

    ai_message_regenerated: (uiElement: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_message_regenerated', {
        ...buildInChatContextProperties(ctx),
        input_method: 'Prompt',
        ui_element: uiElement,
      })
    },

    ai_message_feedback: (uiElement: AiFeedbackUiElement) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_message_feedback', {
        ...buildInChatContextProperties(ctx),
        ui_element: uiElement,
      })
    },

    ai_speech_to_text: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_speech_to_text', {
        ...buildInChatContextProperties(ctx),
        input_method: 'Speech',
        ui_element: 'Voice Mode',
      })
    },

    ai_unrecognized_input: (
      errorDetails: string,
      inputMethod: AiInputMethod,
      uiElement: string,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_unrecognized_input', {
        ...buildInChatContextProperties(ctx),
        error_details: errorDetails,
        input_method: inputMethod,
        ui_element: uiElement,
      })
    },

    ai_videoguide_disabled: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_videoguide_disabled')
    },

    ai_videoguide_enabled: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_videoguide_enabled')
    },

    concept_moment_toast_shown: (momentStartSeconds: number, conceptLabel: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('concept_moment_toast_shown', {
        ...buildInChatContextProperties(ctx),
        moment_start_seconds: momentStartSeconds,
        concept_label: conceptLabel,
      })
    },

    concept_moment_toast_clicked: (momentStartSeconds: number, conceptLabel: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('concept_moment_toast_clicked', {
        ...buildInChatContextProperties(ctx),
        moment_start_seconds: momentStartSeconds,
        concept_label: conceptLabel,
      })
    },

    concept_moment_toast_dismissed: (momentStartSeconds: number, conceptLabel: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('concept_moment_toast_dismissed', {
        ...buildInChatContextProperties(ctx),
        moment_start_seconds: momentStartSeconds,
        concept_label: conceptLabel,
      })
    },
  }
}
